import { createFileRoute, Link } from "@tanstack/react-router";
import { DEFAULT_AUTHOR } from "../content/authors";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Acadly | Study Notes for Indian College Students" },
      {
        name: "description",
        content:
          "Acadly is an independent study-notes site for Indian college students, written by Dhairya, a Delhi University economics graduate. Learn who runs it and what to expect.",
      },
      {
        name: "robots",
        content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      { property: "og:site_name", content: "Acadly" },
      { property: "og:locale", content: "en_IN" },
      { property: "og:type", content: "profile" },
      { property: "og:title", content: "About Acadly | Study Notes for Indian College Students" },
      {
        property: "og:description",
        content:
          "Meet the person behind Acadly and read about the editorial approach that shapes every piece.",
      },
      { property: "og:url", content: "https://acadly.in/about" },
      { property: "og:image", content: "https://acadly.in/og-default.png" },
      { property: "og:image:alt", content: "About Acadly" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Acadly | Study Notes for Indian College Students" },
      {
        name: "twitter:description",
        content: "Meet the person behind Acadly and read about the editorial approach that shapes every piece.",
      },
      { name: "twitter:image", content: "https://acadly.in/og-default.png" },
      { name: "twitter:image:alt", content: "About Acadly" },
    ],
    links: [{ rel: "canonical", href: "https://acadly.in/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Dhairya",
          jobTitle: "Founder, Acadly",
          description:
            "BA (Hons) Economics, University of Delhi. Writes on economics, exam craft and study systems for Indian undergraduates.",
          url: "https://acadly.in/about",
          worksFor: {
            "@type": "Organization",
            name: "Acadly",
            url: "https://acadly.in",
          },
          alumniOf: {
            "@type": "CollegeOrUniversity",
            name: "University of Delhi",
          },
          sameAs: ["https://dhairya.lol"],
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  const a = DEFAULT_AUTHOR;
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 md:py-20 prose-post">
      <div className="stamp mb-6">About</div>
      <h1
        className="text-5xl md:text-6xl leading-[1] tracking-tight font-semibold"
        style={{ fontFamily: "var(--font-display)" }}
      >
        About Acadly
      </h1>
      <p className="mono-label !mt-2">Last updated: 25 July 2026</p>

      <p>
        Acadly is an independent study-notes and study-strategy website built for
        Indian college students. It focuses on the subjects, exam patterns and
        campus questions that actually come up in an undergraduate degree, with
        a bias towards economics and the University of Delhi syllabus because
        that is what the author studied. Everything published here is free to
        read.
      </p>

      <h2>What Acadly is (and what it isn't)</h2>
      <p>
        Acadly is a personal editorial project, not a coaching institute, tuition
        service or degree-granting body. There are no paid courses, no premium
        tiers and no test series. The site does not accept enrolments, host live
        classes or issue certificates. Every article is a written piece designed
        to be read on the web.
      </p>
      <p>
        The goal is simple: give a student the reading a slightly senior peer
        would have handed them a few months before the exam, in clear English,
        with worked examples where they help.
      </p>

      <h2>Sections you will find here</h2>
      <ul>
        <li>
          <Link to="/category/$category" params={{ category: "subject-explainers" }}>
            Subject explainers
          </Link>{" "}
          for concepts that do not always land the first time in class.
        </li>
        <li>
          <Link to="/category/$category" params={{ category: "exam-strategy" }}>
            Exam strategy
          </Link>{" "}
          for reading a question paper and using three hours well.
        </li>
        <li>
          <Link to="/category/$category" params={{ category: "study-systems" }}>
            Study systems
          </Link>{" "}
          for the habits that quietly decide how a semester goes.
        </li>
        <li>
          <Link to="/category/$category" params={{ category: "college-life" }}>
            College life
          </Link>{" "}
          for the practical parts of being a first-year that no one explains.
        </li>
        <li>
          <Link to="/category/$category" params={{ category: "careers" }}>
            Careers
          </Link>{" "}
          for the medium-term question of what an economics degree is for.
        </li>
      </ul>

      <h2>Who writes it</h2>
      <p>
        Acadly is written and edited by{" "}
        <a
          href={a.portfolio}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold underline decoration-grade decoration-2 underline-offset-4 hover:text-grade"
        >
          {a.name}
        </a>
        . {a.credentials} {a.bio} You can view his background and connect on his{" "}
        <a
          href={a.portfolio}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-grade"
        >
          portfolio website
        </a>
        .
      </p>
      <p>
        When an occasional guest piece is published, the writer is named at the
        top of the article along with a short line about their background. No
        articles are published anonymously.
      </p>

      <h2>Editorial approach</h2>
      <p>
        Every article is written or substantially rewritten by a person and read
        end to end before it is published. Drafts sometimes start with an
        outline sketched using AI tools; anything that reaches the site has
        been reviewed, rewritten in a human voice and fact-checked against a
        current reference where the claim depends on a rule, a formula or a
        date.
      </p>
      <p>
        Rules change. University syllabi, exam patterns and admission policies
        move without much notice. When an article makes a claim that depends on
        a rule, it names the source and the date so a reader can verify it. The
        date shown on a post is the date the claim was true, not a promise that
        it still is.
      </p>
      <p>
        Acadly does not accept paid placements, sponsored posts or affiliate
        arrangements at present. If any of that changes in the future, it will
        be disclosed clearly at the top of the affected article.
      </p>

      <h2>How the site earns its keep</h2>
      <p>
        Acadly is an independent editorial project. To remain 100% free for all
        students and support ongoing research and writing, Acadly displays
        unobtrusive advertising through <strong>Google AdSense</strong>. All
        advertisements are clearly labeled and kept strictly separate from editorial
        content. For details on how advertising cookies work and how to manage your
        preferences, please see our <Link to="/privacy-policy">Privacy Policy</Link>.
      </p>

      <h2>Corrections and contact</h2>
      <p>
        Corrections are genuinely welcome. If a fact is wrong, a formula is
        off, or a rule has changed since publication, please write in and it
        will be fixed with a dated update on the article. Read the full{" "}
        <Link to="/corrections">Corrections policy</Link> for how we handle
        updates. General questions, topic requests and feedback are welcome
        too. The <Link to="/contact">contact page</Link> lists every way to
        reach us.
      </p>
    </div>
  );
}